import { FC, useEffect, useState } from "react"
import trashRedIcon from "assets/svg/trash-red.svg"
import { formatFileSize, getFileExtension } from "utils/file-upload"

/**
 * Selected-file row with name, size, animated progress bar, and a delete
 * action that switches to "Uploading..." while `isProcessing`.
 *
 * SRP: render-only. It does not own the file, does not validate, does not
 * cancel the worker. The parent decides what "delete" means by passing
 * `onDelete`.
 *
 * Icon strategy:
 *  - By default we ship two icons (CSV vs Excel) so the common timesheet/
 *    bulk-import case works out of the box.
 *  - For other file types pass `resolveIcon` to map an extension → URL.
 *
 * Usage:
 *   <SelectedFilePreview
 *     file={selectedFile}
 *     isProcessing={isProcessing}
 *     onDelete={() => setFile(null)}
 *   />
 */

export interface SelectedFilePreviewProps {
  file: File
  isProcessing: boolean
  onDelete: () => void
  /**
   * Optional icon resolver. Receives the lower-cased extension (no dot)
   * and the File for fallback decisions. Return an image URL.
   */
  resolveIcon?: (extension: string, file: File) => string
}

const CSV_ICON =
  "https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/a9e56ece-0523-456e-843b-05d67c4291a8.png"
const EXCEL_ICON =
  "https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/12fbba5c-8dd9-40ce-9849-85c0e871f941.png"

const defaultResolveIcon = (extension: string): string =>
  extension === "csv" ? CSV_ICON : EXCEL_ICON

const SelectedFilePreview: FC<SelectedFilePreviewProps> = ({
  file,
  isProcessing,
  onDelete,
  resolveIcon = defaultResolveIcon,
}) => {
  // Animate the progress bar to ~95% while processing, then snap to 100%
  // on completion. Pure UX affordance — no actual progress data from the worker.
  const [width, setWidth] = useState("0%")

  useEffect(() => {
    if (isProcessing) {
      setWidth("0%")
      const id = requestAnimationFrame(() => setWidth("95%"))
      return () => cancelAnimationFrame(id)
    }
    setWidth("100%")
  }, [isProcessing])

  const iconSrc = resolveIcon(getFileExtension(file), file)

  return (
    <div className="relative overflow-hidden flex items-center justify-between border border-[#E2E8F0] rounded-lg p-3 mb-3">
      <div
        className="absolute inset-y-0 left-0 bg-[#F1F5F9]"
        style={{
          width,
          transition: isProcessing
            ? "width 2000ms linear"
            : "width 250ms ease-out",
        }}
      />

      <div className="relative flex items-center gap-2 min-w-0">
        <img
          src={iconSrc}
          className="w-10 h-10 object-contain shrink-0"
          alt="file-icon"
        />

        <div className="flex flex-col gap-1.5 min-w-0">
          <p className="truncate font-circular text-base leading-normal text-[#020618]">
            {file.name}
          </p>
          <p className="font-circular text-sm leading-normal text-[#62748E]">
            {formatFileSize(file.size)}MB
          </p>
        </div>
      </div>

      {isProcessing ? (
        <p className="relative font-circular text-sm leading-normal text-[#62748E] whitespace-nowrap pl-3">
          Uploading...
        </p>
      ) : (
        <button
          type="button"
          onClick={onDelete}
          className="relative flex items-center gap-1.5 font-circular text-sm font-medium text-[#EB3949] whitespace-nowrap pl-3 hover:opacity-80 transition-opacity"
        >
          <img
            src={trashRedIcon}
            width={14}
            height={14}
            alt=""
          />
          Delete
        </button>
      )}
    </div>
  )
}

export default SelectedFilePreview
